import { z } from "zod";
import { idSchema, type IdPrefix } from "./ids.js";
import { auditActorSchema, instantSchema } from "./primitives.js";

function subject<K extends string, P extends IdPrefix>(kind: K, prefix: P) {
  return z.strictObject({ kind: z.literal(kind), id: idSchema(prefix) });
}

export const subjectRefSchema = z.discriminatedUnion("kind", [
  subject("user", "usr"),
  subject("invitation", "inv"),
  subject("machine_credential", "mcr"),
  subject("auto_join_domain", "adm"),
  subject("profile", "prf"),
  subject("organisation", "org"),
  subject("affiliation", "aff"),
  subject("presence", "prs"),
  subject("suggested_source", "sug"),
  subject("coverage_plan", "pln"),
  subject("plan_revision", "plr"),
  subject("recheck", "rck"),
  subject("profile_merge", "mrg"),
  subject("refresh_proposal", "rfp"),
  subject("source", "src"),
  subject("scan", "scn"),
  subject("gap", "gap"),
  subject("publishing_break", "brk"),
  subject("item", "itm"),
  subject("source_version", "ver"),
  subject("artifact", "art"),
  subject("capture_manifest", "man"),
  subject("attempt", "att"),
  subject("rights_record", "rgt"),
  subject("route", "rte"),
  subject("backfill_job", "bfl"),
  subject("history_extension", "ext"),
  subject("platform_issue", "pwi"),
  subject("transcription_job", "trj"),
  subject("candidate_result", "res"),
  subject("speaker", "spk"),
  subject("naming_decision", "nmd"),
  subject("profile_decision", "pmd"),
  subject("passage_check", "chk"),
  subject("communication", "com"),
  subject("evidence_revision", "rev"),
  subject("unit", "unt"),
  subject("speaker_assignment", "asg"),
  subject("copy_link", "cpy"),
  subject("alignment_range", "aln"),
  subject("correction", "cor"),
  subject("carry_over", "cov"),
  subject("reported_problem", "rpt"),
  subject("readiness_assessment", "ass"),
  subject("edit_lease", "lse"),
  subject("policy_version", "pol"),
  subject("communication_relation", "spl"),
  subject("optional_check", "ock"),
  subject("cost_entry", "cst"),
  subject("task", "tsk"),
  subject("pause", "pau"),
  subject("bulk_job", "job"),
  subject("budget_category", "bud"),
  subject("rule_value", "rvs"),
  subject("drill", "drl"),
  subject("dependency_date", "dep"),
  subject("audit_entry", "aud"),
]);
export type SubjectRef = z.infer<typeof subjectRefSchema>;
export type SubjectKind = SubjectRef["kind"];

export const AUDIT_ACTION_MODULES = [
  "access",
  "profiles",
  "collection",
  "transcription",
  "evidence",
  "operations",
  "platform",
] as const;
export const auditActionSchema = z
  .string()
  .regex(new RegExp(`^(${AUDIT_ACTION_MODULES.join("|")})\\.[a-z][a-z_]*$`), "expected <module>.<action>");

export const auditValuesSchema = z.record(z.string(), z.json());

export const SIGN_IN_REFUSED_ACTION = "access.sign_in_refused";

export const auditEntryRecordSchema = z
  .strictObject({
    id: idSchema("aud"),
    actor: auditActorSchema,
    action: auditActionSchema,
    subject: subjectRefSchema.nullable(),
    reason: z.string().trim().min(1).nullable(),
    before: auditValuesSchema.nullable(),
    after: auditValuesSchema.nullable(),
    at: instantSchema,
  })
  .refine((e) => e.actor.kind !== "anonymous" || e.action === SIGN_IN_REFUSED_ACTION, {
    message: "only refused sign-ins have an anonymous actor",
    path: ["actor"],
  });
export type AuditEntryRecord = z.infer<typeof auditEntryRecordSchema>;
